/**
 * Admin Portal → Codes: Coach Cedar's own codes (the house partner, migration 1876) and code
 * batches (migration 1877). The pure part — types, labels, request bodies — so jest can pin it
 * without a DOM. docs/architecture/38-PARTNER-ATTRIBUTION.md §16.
 *
 * Web-only (the admin SPA).
 */

export const CODES_API = '/api/admin/codes';

export type BatchOwner = 'partner' | 'house' | 'campaign';
export type BatchFamily = 'summer50' | 'strategic' | 'referral' | 'partner' | 'house';
export type BatchCreatedVia = 'admin' | 'api' | 'cron' | 'system' | 'manychat' | 'store_import';

/** services/code_batches.py OWNERS / CREATED_VIA (the backend's vocabulary; a jest test pins the lists). */
export const BATCH_OWNERS: BatchOwner[] = ['partner', 'house', 'campaign'];
export const BATCH_CREATED_VIA: BatchCreatedVia[] = ['admin', 'api', 'cron', 'system', 'manychat', 'store_import'];
/** migration 1877's CHECKs on code_batches.purpose / notes. */
export const PURPOSE_MAX_LEN = 200;
export const NOTES_MAX_LEN = 2000;
export const BATCH_PAGE_SIZE = 50;
export const BATCH_CODES_PAGE_SIZE = 100;

export interface BatchRow {
  id: string;
  owner: BatchOwner;
  family: BatchFamily;
  partner_id: string | null;
  partner_code: string | null;
  partner_name: string | null;
  tag_id: string | null;
  tag: string | null;
  kind: string | null;
  created_via: BatchCreatedVia;
  created_by: string | null;
  purpose: string | null;
  notes: string | null;
  requested_count: number | null;
  api_key_id: string | null;
  api_day: string | null;
  created_at: string;
  expires_at: string | null;
  /** DERIVED from the member rows when read — never stored on the batch. */
  codes: number;
  redemptions: number;
  states: Record<string, number>;
}

export interface BatchCode {
  id: string;
  code: string;
  kind: string;
  state: string;
  redemptions: number;
  max_redemptions: number | null;
  expires_at: string | null;
  created_at: string;
  redeemed_at?: string | null;
  allocated_at?: string | null;
  revoked_at: string | null;
  tag?: string | null;
  wave?: string | null;
  external_ref?: string | null;
}

export interface BatchFilters {
  owner?: BatchOwner | '';
  createdVia?: BatchCreatedVia | '';
  partnerId?: string;
  tagId?: string;
  limit?: number;
  offset?: number;
}

export interface HousePartner {
  id: string;
  code: string;
  legal_name: string;
  display_name: string;
  status: 'active' | 'paused' | 'ended';
  created_at: string;
}

export interface HouseOverview {
  house: HousePartner | null;
  tags: unknown[];
  house_codes_enabled: boolean;
  redemption_live: boolean;
}

export interface CounterCheck {
  ok: boolean;
  partner_codes: {
    explained_by_deleted_accounts: number;
    drift: number;
    uses_missing_from_counter: number;
    drift_sample: string[];
  };
  apple_batches: { drift: number; drift_sample: Array<{ batch: string; counter: number; rows: number }> };
  unbatched: Record<string, number>;
  /** Codes with no batch, all tables: information, never a failed check (``ok`` ignores it). */
  unbatched_total?: number;
  checked_at?: string;
}

/** What "Adopt unbatched codes" answers: per code table, how many codes it filed and how many are left. */
export interface AdoptResult {
  adopted: Record<string, number>;
  remaining: Record<string, number>;
}

/** Who may run "Adopt unbatched codes" (services/code_batches.py ADOPT_ROLES): it writes every family at once. */
export const ADOPT_ROLES = ['super_admin'] as const;

export function canAdopt(role: string | null | undefined): boolean {
  return (ADOPT_ROLES as readonly string[]).includes(String(role || ''));
}

export function unbatchedTotal(c: CounterCheck): number {
  return Object.values(c.unbatched || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);
}

/** "Adopt unbatched codes" in the admin's words. */
export function adoptSummary(r: AdoptResult): string {
  const filed = Object.values(r.adopted || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);
  const left = Object.values(r.remaining || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);
  const head = filed ? `Filed ${filed} code${filed === 1 ? '' : 's'} into "backfilled" batches, one per day.` : 'Nothing to file: every code already has a batch.';
  return left ? `${head} ${left} still without a batch.` : head;
}

/** The list's query string: only the filters that are set. */
export function batchesQuery(f: BatchFilters): string {
  const qs = new URLSearchParams();
  if (f.owner) qs.set('owner', f.owner);
  if (f.createdVia) qs.set('created_via', f.createdVia);
  if (f.partnerId) qs.set('partner_id', f.partnerId);
  if (f.tagId) qs.set('tag_id', f.tagId);
  qs.set('limit', String(f.limit ?? BATCH_PAGE_SIZE));
  qs.set('offset', String(f.offset ?? 0));
  return `?${qs.toString()}`;
}

/** Whose codes a batch holds, in the admin's words. The house is always "Coach Cedar". */
export function batchOwnerLabel(b: Pick<BatchRow, 'owner' | 'family' | 'partner_name' | 'partner_code' | 'tag'>): string {
  if (b.owner === 'house') return b.tag ? `Coach Cedar · ${b.tag}` : 'Coach Cedar';
  if (b.owner === 'campaign') {
    return b.family === 'summer50' ? 'Summer50' : b.family === 'strategic' ? 'Strategic (one-off)' : 'Referral codes';
  }
  const who = b.partner_name || b.partner_code || 'Partner';
  return b.tag ? `${who} · ${b.tag}` : who;
}

export const CREATED_VIA_LABELS: Record<BatchCreatedVia, string> = {
  admin: 'Admin',
  api: 'Partner API',
  cron: 'Scheduled job',
  system: 'System',
  manychat: 'ManyChat',
  store_import: 'Store import',
};

/** "3 active · 1 redeemed" — the states in a stable order, zeros left out. */
const STATE_ORDER = ['active', 'pooled', 'allocated', 'issued', 'redeemed', 'used_up', 'expired', 'revoked', 'replaced'];
export function statesSummary(states: Record<string, number> | null | undefined): string {
  const entries = Object.entries(states || {}).filter(([, n]) => Number(n) > 0);
  entries.sort(([a], [b]) => {
    const ia = STATE_ORDER.indexOf(a);
    const ib = STATE_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
  });
  return entries.length ? entries.map(([s, n]) => `${n} ${s.replace(/_/g, ' ')}`).join(' · ') : 'no codes';
}

/**
 * The batch metadata a code form sends (migration 1877): the purpose and notes, trimmed, only
 * when set. Returns the fields and any error in the admin's words.
 */
export function batchMeta(purpose: string, notes: string): { fields: Record<string, string>; errors: string[] } {
  const fields: Record<string, string> = {};
  const errors: string[] = [];
  const p = purpose.trim();
  const n = notes.trim();
  if (p.length > PURPOSE_MAX_LEN) errors.push(`Batch purpose: at most ${PURPOSE_MAX_LEN} characters.`);
  if (n.length > NOTES_MAX_LEN) errors.push(`Batch notes: at most ${NOTES_MAX_LEN} characters.`);
  if (p) fields.batch_purpose = p;
  if (n) fields.batch_notes = n;
  return { fields, errors };
}

export type PartnerDetailTab = 'overview' | 'tags' | 'contacts' | 'codes' | 'api' | 'statements' | 'settings';
const ALL_TABS: Array<[PartnerDetailTab, string]> = [
  ['overview', 'Overview'], ['tags', 'Links & tags'], ['contacts', 'Contacts'], ['codes', 'Codes'], ['api', 'API keys'],
  ['statements', 'Statements'], ['settings', 'Settings'],
];

/**
 * The partner detail page's tabs. The Coach Cedar house partner never has contacts, API keys,
 * statements or editable settings (migration 1876; the partner launch plan's contacts are a
 * partner's own list): its page shows the funnel, its tags and its codes only.
 */
export function partnerDetailTabs(isHouse: boolean): Array<[PartnerDetailTab, string]> {
  return isHouse ? ALL_TABS.filter(([t]) => t === 'overview' || t === 'tags' || t === 'codes') : ALL_TABS;
}

/** A new Coach Cedar tag: POST /api/admin/codes/house/tags. No bounty field — a house tag pays none. */
export function houseTagBody(f: {
  tag: string; name: string; trial: number; dm: string; dmm: string; da: string;
}): Record<string, unknown> {
  const body: Record<string, unknown> = { tag: f.tag, name: f.name.trim(), trial_days: f.trial };
  if (f.dm) {
    body.discount_monthly_pct = Number(f.dm);
    body.discount_monthly_months = Number(f.dmm);
  }
  if (f.da) body.discount_annual_pct = Number(f.da);
  return body;
}

export type CheckTone = 'warn' | 'info' | 'ok';

/** The counter check in the admin's words, each finding with its tone: counter drift is a
 * warning; codes with no batch and deleted-redeemer differences are information (neither moves
 * money — the server's ``ok`` ignores them). */
export function counterCheckFindings(c: CounterCheck): Array<{ text: string; tone: CheckTone }> {
  const out: Array<{ text: string; tone: CheckTone }> = [];
  if (c.partner_codes.drift) {
    out.push({ tone: 'warn', text: `${c.partner_codes.drift} partner code${c.partner_codes.drift === 1 ? '' : 's'} counted FEWER uses than their redemption rows (${c.partner_codes.uses_missing_from_counter} missing) — investigate.` });
  }
  if (c.apple_batches.drift) {
    out.push({ tone: 'warn', text: `${c.apple_batches.drift} App Store code batch${c.apple_batches.drift === 1 ? '' : 'es'} whose "codes imported" disagrees with the codes in inventory.` });
  }
  for (const [table, n] of Object.entries(c.unbatched || {})) {
    if (n) out.push({ tone: 'info', text: `${n} ${table === 'campaign_codes' ? 'Summer50 / strategic / referral' : 'partner / Coach Cedar'} code${n === 1 ? '' : 's'} with no batch (information, not a fault: "Adopt unbatched codes" files them).` });
  }
  if (c.partner_codes.explained_by_deleted_accounts) {
    out.push({ tone: 'info', text: `${c.partner_codes.explained_by_deleted_accounts} code${c.partner_codes.explained_by_deleted_accounts === 1 ? '' : 's'} counted more uses than their rows: redeemers who deleted their accounts (expected).` });
  }
  if (!out.length) out.push({ tone: 'ok', text: 'Every counter matches its rows, and every code has a batch.' });
  return out;
}

/** The findings' words alone. */
export function counterCheckLines(c: CounterCheck): string[] {
  return counterCheckFindings(c).map((f) => f.text);
}

export function shortId(id: string | null | undefined): string {
  return id ? String(id).slice(0, 8) : '—';
}
